import { MAX_PDF_PAGES } from '@oneapp/shared';

/**
 * Photos of invoices, made ready on the phone before they go up (Phase 8): each is drawn again as a
 * JPEG no bigger than the AI reads (2,000 pixels on the long side), the right way up, without the
 * camera's hidden details (location, phone model). iPhone photos (HEIC) come out as JPEGs too, and a
 * PDF's pages are drawn as pictures, so the server only ever gets pictures.
 */

/** Long enough for small print, small enough for mobile data (about 300–600 KB a photo). */
export const LONG_SIDE = 2000;
const QUALITY = 0.85;

export type PrepareProblem =
  /** Not a photo or a PDF. */
  | 'type'
  /** A photo this browser can't open (HEIC outside Safari, a damaged file). */
  | 'unreadable'
  | 'pdf_password'
  | 'pdf_pages';

export class PrepareError extends Error {
  constructor(readonly problem: PrepareProblem) {
    super(problem);
    this.name = 'PrepareError';
  }
}

/** A picture ready to upload. */
export interface PreparedPage {
  blob: Blob;
  /** "IMG_2041.jpg", or "bills.pdf · page 2". */
  name: string;
  width: number;
  height: number;
}

/** A file as chosen: a photo is one page; a PDF is its pages (read together, as one document). */
export interface PreparedFile {
  name: string;
  kind: 'photo' | 'pdf';
  pages: PreparedPage[];
}

const isPdf = (file: File) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
const isImage = (file: File) =>
  file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif|gif|bmp)$/i.test(file.name);

export async function prepareFile(file: File): Promise<PreparedFile> {
  if (isPdf(file)) return { name: file.name, kind: 'pdf', pages: await pdfPages(file) };
  if (!isImage(file)) throw new PrepareError('type');
  return { name: file.name, kind: 'photo', pages: [await photoPage(file)] };
}

/** A name for the JPEG: the photo's own, ending in .jpg. */
function jpegName(name: string): string {
  const base = name.replace(/\.[a-z0-9]+$/i, '') || 'photo';
  return `${base}.jpg`;
}

async function photoPage(file: File): Promise<PreparedPage> {
  const url = URL.createObjectURL(file);
  try {
    const image = await decode(file, url);
    const width = 'naturalWidth' in image ? image.naturalWidth : image.width;
    const height = 'naturalHeight' in image ? image.naturalHeight : image.height;
    if (!width || !height) throw new PrepareError('unreadable');
    const drawn = await drawJpeg(image, width, height);
    if ('close' in image) image.close();
    return { ...drawn, name: jpegName(file.name) };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** The photo as pixels, turned the way the camera meant it; Safari's <img> opens HEIC where nothing else does. */
async function decode(file: File, url: string): Promise<ImageBitmap | HTMLImageElement> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    try {
      await image.decode();
    } catch {
      throw new PrepareError('unreadable');
    }
    return image;
  }
}

async function drawJpeg(
  source: CanvasImageSource,
  width: number,
  height: number,
): Promise<{ blob: Blob; width: number; height: number }> {
  const scale = Math.min(1, LONG_SIDE / Math.max(width, height));
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const context = canvas.getContext('2d');
  if (!context) throw new PrepareError('unreadable');
  // A white page behind a PNG with see-through parts.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, w, h);
  context.drawImage(source, 0, 0, w, h);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
  if (!blob) throw new PrepareError('unreadable');
  return { blob, width: w, height: h };
}

/** A PDF's pages as pictures (pdf.js loads only when a PDF is chosen). */
async function pdfPages(file: File): Promise<PreparedPage[]> {
  const pdfjs = await import('pdfjs-dist');
  const { default: worker } = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = worker;
  // pdf.js 6 has no eval-based font code at all (CVE-2024-4367 was in that path).
  const loading = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  let pdf: Awaited<(typeof loading)['promise']>;
  try {
    pdf = await loading.promise;
  } catch (error) {
    throw new PrepareError((error as { name?: string }).name === 'PasswordException' ? 'pdf_password' : 'unreadable');
  }
  try {
    if (pdf.numPages > MAX_PDF_PAGES) throw new PrepareError('pdf_pages');
    const pages: PreparedPage[] = [];
    for (let n = 1; n <= pdf.numPages; n++) {
      const page = await pdf.getPage(n);
      const size = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: LONG_SIDE / Math.max(size.width, size.height) });
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      await page.render({ canvas, viewport, background: '#ffffff' }).promise;
      const drawn = await drawJpeg(canvas, canvas.width, canvas.height);
      pages.push({ ...drawn, name: `${file.name} · page ${n}` });
      page.cleanup();
    }
    return pages;
  } finally {
    await loading.destroy();
  }
}
